import React, { useState, useEffect } from 'react';
import firebaseDataService from '../../../services/FirebaseDataService';
import { getCurrencySettings } from '../../../services/migration/companyDataService';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Box,
  Paper,
  CircularProgress,
  TextField,
  IconButton,
  MenuItem,
  Select,
  Alert,
  FormControl,
  InputLabel,
  InputAdornment,
  Snackbar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import GuideButton from '../../common/guide/GuideButton';
import { useAuth } from '../../../contexts/AuthContext';
import { ACCOUNTING_API, handleApiError } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import PaymentReceiptModal from './PaymentReceiptModal';
import { useTranslation } from 'react-i18next';
import ProjectSelector from '../shared/ProjectSelector';
import { AppDatePicker } from '../../common/AppDatePicker';

// Ödeme yöntemleri
const paymentMethods = [
  { id: 'cash', tr: 'Nakit', en: 'Cash' },
  { id: 'credit_card', tr: 'Kredi Kartı', en: 'Credit Card' },
  { id: 'bank_transfer', tr: 'Havale/EFT', en: 'Bank Transfer/EFT' },
  { id: 'check', tr: 'Çek', en: 'Check' },
  { id: 'pos', tr: 'Pos', en: 'POS' },
  { id: 'accrual', tr: 'Tahakkuk', en: 'Accrual' },
];

interface Currency {
  id: string;
  name: string;
  symbol: string;
  code: string;
  isDefault?: boolean;
}

interface Cashbox {
  id: string;
  name: string;
  description?: string;
  currency: string;
  balance: number;
  createdAt?: string;
  isActive?: boolean;
  branchId?: string | null;
  isDefault?: boolean;
  hasTransactions?: boolean;
  currencyCode?: string;
  currencySymbol?: string;
}

interface BankAccount {
  id: string;
  bankName: string;
  accountName?: string;
  branchName?: string;
  accountNumber: string;
  iban?: string;
  currency: string;
  balance: number;
  isActive?: boolean;
  isPosAccount?: boolean; 
  isCheckAccount?: boolean;
  currencySymbol?: string;
  parentBankAccountId?: string | null;
  isSubAccount?: boolean;
}

interface CollectPaymentModalProps {
  open: boolean;
  onClose: () => void;
  accountId: string;
  accountName: string;
  companyId: string | null;
  onSuccess?: () => void;
  /** İşlemi yapan ekranın şubesi (manager panelinde kilitli şube) */
  branchId?: string | null;
  /** true ise şube seçimi kilitlidir (manager paneli) */
  branchLocked?: boolean;
}

const CollectPaymentModal: React.FC<CollectPaymentModalProps> = ({
  open,
  onClose,
  accountId,
  accountName,
  companyId,
  onSuccess,
  branchId = null,
  branchLocked = false
}) => {
  const { userDetails, branches } = useAuth();
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Form states
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [cashboxes, setCashboxes] = useState<Cashbox[]>([]);
  const [filteredCashboxes, setFilteredCashboxes] = useState<Cashbox[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [filteredBankAccounts, setFilteredBankAccounts] = useState<BankAccount[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('cash');
  const [selectedCashbox, setSelectedCashbox] = useState<Cashbox | null>(null);
  const [selectedBankAccount, setSelectedBankAccount] = useState<BankAccount | null>(null);
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState('');
  // PYS: hareketi bir projeye bağlar (opsiyonel)
  const [projectId, setProjectId] = useState<number | null>(null);
  const [documentNo, setDocumentNo] = useState<string>(''); // Belge numarası
  const [accountBalance, setAccountBalance] = useState<number>(0);
  // Şube seçimi ('' = Genel; manager panelinde kilitli)
  const [selectedBranchId, setSelectedBranchId] = useState<string>('');
  // ✅ İşlem tarihi (geri/ileri tarihli işlem desteği) — YYYY-MM-DD formatında string
  const [transactionDate, setTransactionDate] = useState<string>(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  });
  
  // Tahsilat makbuzu modalı için state
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [completedTransaction, setCompletedTransaction] = useState<any>(null);

  useEffect(() => {
    if (open && companyId && accountId) {
      loadAccountDetails();
    }
  }, [open, companyId, accountId]);

  // Otomatik belge numarası oluştur
  const generateDocumentNo = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    
    // Format: THB-YYYYMMDD-HHMMSS
    return `THB-${year}${month}${day}-${hours}${minutes}${seconds}`;
  };

  useEffect(() => {
    if (open) {
      setError(null);
      setSuccess(false);
      setSuccessMessage('');
      setSelectedCurrency(null);
      setSelectedPaymentMethod('cash');
      setSelectedCashbox(null);
      setSelectedBankAccount(null);
      setAmount('');
      setDescription('');
      setProjectId(null);
      setSelectedBranchId(branchId || ''); // Şube: prop'tan gelen değerle başlat
      setDocumentNo(generateDocumentNo()); // Otomatik belge numarası oluştur
      // ✅ İşlem tarihini bugüne sıfırla
      const t = new Date();
      setTransactionDate(`${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`);
      loadCurrencies();
    }
  }, [open, companyId]);

  useEffect(() => {
    if (selectedCurrency) {
      loadCashboxes();
      loadBankAccounts();
    }
  }, [selectedCurrency, companyId]);

  useEffect(() => {
    if (selectedCurrency && cashboxes.length > 0) {
      const filtered = cashboxes.filter(cashbox => cashbox.currency === selectedCurrency.code);
      setFilteredCashboxes(filtered);
    }
  }, [selectedCurrency, cashboxes]);

  useEffect(() => {
    if (selectedCurrency && bankAccounts.length > 0) {
      const filtered = bankAccounts.filter(account => account.currency === selectedCurrency.code);
      setFilteredBankAccounts(filtered);
    }
  }, [selectedCurrency, bankAccounts]);

  useEffect(() => {
    if (selectedPaymentMethod !== 'cash') {
      setSelectedCashbox(null);
    }
    
    if (selectedPaymentMethod === 'cash') {
      setSelectedBankAccount(null);
    }
  }, [selectedPaymentMethod]);

  const loadCurrencies = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const currencyData = await getCurrencySettings(companyId!);
      
      const settings: {[code: string]: boolean} = { TRY: true };
      
      if (currencyData) {
        Object.entries(currencyData).forEach(([code, active]: [string, any]) => {
          settings[code] = Boolean(active);
        });
      }
      
      const availableCurrencies: string[] = [
        'TRY', 'USD', 'EUR', 'GBP', 'CHF', 'JPY', 'AUD', 'CAD', 'RUB', 'AED', 'SAR', 'NOK', 'DKK', 'SEK'
      ];
      
      const activeCurrencyCodes = availableCurrencies.filter(code => settings[code]);
      
      const currenciesData: Currency[] = activeCurrencyCodes.map(code => ({
        id: code,
        name: getCurrencyName(code),
        symbol: getCurrencySymbol(code),
        code: code,
        isDefault: code === 'TRY'
      }));
      
      setCurrencies(currenciesData);
      
      const defaultCurrency = currenciesData.find(c => c.isDefault === true);
      if (defaultCurrency) {
        setSelectedCurrency(defaultCurrency);
      } else if (currenciesData.length > 0) {
        setSelectedCurrency(currenciesData[0]);
      }
      
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      setError(tt('Para birimleri yüklenirken hata oluştu: ', 'An error occurred while loading currencies: ') + err.message);
    }
  };

  const getCurrencySymbol = (code: string): string => {
    switch(code) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'JPY': return '¥';
      case 'CHF': return 'Fr.';
      case 'AUD': return 'A$';
      case 'CAD': return 'C$';
      case 'CNY': return '¥';
      default: return code;
    }
  };

  const getCurrencyName = (code: string): string => {
    switch(code) {
      case 'TRY': return tt('Türk Lirası', 'Turkish Lira');
      case 'USD': return tt('Amerikan Doları', 'US Dollar');
      case 'EUR': return tt('Euro', 'Euro');
      case 'GBP': return tt('İngiliz Sterlini', 'British Pound');
      case 'JPY': return tt('Japon Yeni', 'Japanese Yen');
      case 'CHF': return tt('İsviçre Frangı', 'Swiss Franc');
      case 'AUD': return tt('Avustralya Doları', 'Australian Dollar');
      case 'CAD': return tt('Kanada Doları', 'Canadian Dollar');
      case 'CNY': return tt('Çin Yuanı', 'Chinese Yuan');
      default: return code;
    }
  };

  const loadCashboxes = async () => {
    try {
      setLoading(true);
      
      const response = await accountingApi.get(ACCOUNTING_API.CASHBOX_BY_COMPANY);

      const result = response.data;
      const sqlCashboxes = result.data || result;
      
      if (!sqlCashboxes || sqlCashboxes.length === 0) {
        setCashboxes([]);
        setFilteredCashboxes([]);
        setLoading(false);
        return;
      }
      
      const cashboxList: Cashbox[] = sqlCashboxes
        .filter((data: any) => data.isActive !== false)
        .map((data: any) => {
          const currencyCode = data.currency || 'TRY';
          
          return {
            id: `cashbox_${data.id}`,
            name: data.name || '',
            description: data.description || '',
            currency: currencyCode,
            currencyCode: currencyCode,
            currencySymbol: getCurrencySymbol(currencyCode),
            balance: data.balance || 0,
            createdAt: data.createdAt || '',
            isActive: data.isActive !== false,
            branchId: data.branchId || null,
            isDefault: data.isDefault || false,
            hasTransactions: data.hasTransactions || false
          };
        });
      
      cashboxList.sort((a, b) => {
        if (a.name === "Genel Kasa") return -1;
        if (b.name === "Genel Kasa") return 1;
        return a.name.localeCompare(b.name);
      });
      
      setCashboxes(cashboxList);
      
      if (selectedCurrency) {
        const filtered = cashboxList.filter(cashbox => cashbox.currency === selectedCurrency.code);
        setFilteredCashboxes(filtered);
      }
      
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      setError(tt('Kasalar yüklenirken hata oluştu: ', 'An error occurred while loading cashboxes: ') + err.message);
    }
  };

  const loadBankAccounts = async () => {
    try {
      setLoading(true);
      
      const response = await accountingApi.get(ACCOUNTING_API.BANK_ACCOUNT_BY_COMPANY);

      const result = response.data;
      const sqlBankAccounts = result.data || result;
      
      if (!sqlBankAccounts || sqlBankAccounts.length === 0) {
        setBankAccounts([]);
        setFilteredBankAccounts([]);
        setLoading(false);
        return;
      }
      
      const accountsList: BankAccount[] = [];
      
      sqlBankAccounts.forEach((data: any) => {
        const currencyCode = data.currency || 'TRY';
        
        accountsList.push({
          id: `bank_${data.id}`,
          bankName: data.bankName || '',
          branchName: data.branchName || '',
          accountNumber: data.accountNumber || '',
          iban: data.iban || '',
          currency: currencyCode,
          currencySymbol: getCurrencySymbol(currencyCode),
          balance: data.balance || 0,
          isActive: data.isActive !== false,
          isPosAccount: data.isPosAccount || false,
          isCheckAccount: data.isCheckAccount || false,
          parentBankAccountId: null,
          isSubAccount: false
        });
        
        if (data.subAccounts && data.subAccounts.length > 0) {
          data.subAccounts.forEach((subAccount: any) => {
            const subCurrencyCode = subAccount.currency || 'TRY';
            
            accountsList.push({
              id: `sub_${subAccount.id}`,
              bankName: data.bankName + ' - ' + (subAccount.accountName || 'Alt Hesap'),
              accountName: subAccount.accountName || 'Alt Hesap',
              branchName: data.branchName || '',
              accountNumber: subAccount.accountNumber || '',
              iban: subAccount.iban || '',
              currency: subCurrencyCode,
              currencySymbol: getCurrencySymbol(subCurrencyCode),
              balance: subAccount.balance || 0,
              isActive: subAccount.isActive !== false,
              isPosAccount: subAccount.isPosAccount || false,
              isCheckAccount: subAccount.isCheckAccount || false,
              parentBankAccountId: data.id.toString(),
              isSubAccount: true
            });
          });
        }
      });
      
      accountsList.sort((a, b) => a.bankName.localeCompare(b.bankName));
      setBankAccounts(accountsList);
      
      if (selectedCurrency) {
        const filtered = accountsList.filter(account => account.currency === selectedCurrency.code);
        setFilteredBankAccounts(filtered);
      }
      
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      setError(tt('Banka hesapları yüklenirken hata oluştu: ', 'An error occurred while loading bank accounts: ') + err.message);
    }
  };

  const loadAccountDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('[CollectPaymentModal] 🔍 loadAccountDetails başladı:', { companyId, accountId });
      
      // ✅ MULTI-TENANT: companyId parametresi ekle
      if (!companyId) {
        throw new Error('CompanyId bulunamadı');
      }
      
      // 🔥 YENİ: SQL API'den direkt cari hesap bilgisini çek
      const response = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_ID(parseInt(accountId)));

      const result = response.data;
      const accountData = result.data || result;
      
      console.log('[CollectPaymentModal] 📦 SQL API sonucu:', accountData);
      
      if (accountData) {
        const creditBalance = accountData.creditBalance !== undefined ? accountData.creditBalance : (accountData.balance || 0);
        const debitBalance = accountData.debitBalance || 0;
        const calculatedBalance = creditBalance - debitBalance;
        
        setAccountBalance(calculatedBalance);
      } else {
        setAccountBalance(0);
        setError(tt('Cari hesap bilgileri bulunamadı.', 'Current account details not found.'));
      }
      
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      setError(tt('Cari hesap bilgileri yüklenirken hata oluştu: ', 'An error occurred while loading current account details: ') + err.message);
    }
  };

  const formatCurrency = (amount: number, currencyCode: string = 'TRY') => {
    return new Intl.NumberFormat(localeCode, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Türk formatında sayı formatlama (1.000,00) — DepositModal/WithdrawModal ile aynı pattern
  const formatTurkishNumber = (value: string): string => {
    if (!value) return '';
    const cleanValue = value.replace(/[^0-9,]/g, '');
    const parts = cleanValue.split(',');
    const integerPart = parts[0];
    const decimalPart = parts[1] || '';
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const limitedDecimal = decimalPart.slice(0, 2);
    if (cleanValue.includes(',')) {
      return `${formattedInteger},${limitedDecimal}`;
    }
    return formattedInteger;
  };

  // Formatı temizle ve sayıya çevir (1.000,50 → 1000.5)
  const parseFormattedNumber = (value: string): number => {
    if (!value) return 0;
    const cleanValue = value.replace(/\./g, '').replace(',', '.');
    return parseFloat(cleanValue) || 0;
  };

  // Sayısal girdi kontrolü ve formatlama (input onChange handler)
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value && !/^[0-9.,]*$/.test(value)) return;
    if ((value.match(/,/g) || []).length > 1) return;
    if (value.includes(',')) {
      const parts = value.split(',');
      if (parts[1] && parts[1].length > 2) return;
    }
    setAmount(formatTurkishNumber(value));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!companyId || !accountId || !selectedCurrency) {
        setError(tt('Gerekli bilgiler eksik.', 'Required information is missing.'));
        setLoading(false);
        return;
      }
      
      // Tahakkuk için kasa/banka kontrolü gerekmez
      if (selectedPaymentMethod === 'cash' && !selectedCashbox) {
        setError(tt('Lütfen bir kasa seçiniz.', 'Please select a cashbox.'));
        setLoading(false);
        return;
      }
      
      if (selectedPaymentMethod !== 'cash' && selectedPaymentMethod !== 'accrual' && !selectedBankAccount) {
        setError(tt('Lütfen bir banka hesabı seçiniz.', 'Please select a bank account.'));
        setLoading(false);
        return;
      }
      
      const amountValue = parseFormattedNumber(amount);
      if (isNaN(amountValue) || amountValue <= 0) {
        setError(tt('Geçerli bir miktar giriniz.', 'Please enter a valid amount.'));
        setLoading(false);
        return;
      }
      
      // Belge numarası kontrolü
      if (!documentNo || documentNo.trim() === '') {
        setError(tt('Belge numarası zorunludur. Lütfen belge numarası giriniz.', 'Document number is required. Please enter a document number.'));
        setLoading(false);
        return;
      }

      // ✅ İşlem tarihi parse + validasyon + 30 günden uzak için yumuşak uyarı
      const txDateParts = transactionDate.split('-');
      if (txDateParts.length !== 3) {
        setError(tt('Geçerli bir işlem tarihi seçiniz.', 'Please select a valid transaction date.'));
        setLoading(false);
        return;
      }
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const selected = new Date(Number(txDateParts[0]), Number(txDateParts[1]) - 1, Number(txDateParts[2]));
      if (isNaN(selected.getTime())) {
        setError(tt('Geçerli bir işlem tarihi seçiniz.', 'Please select a valid transaction date.'));
        setLoading(false);
        return;
      }
      const diffDays = Math.round((selected.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      const isBackdated = diffDays < 0;
      const isFuture = diffDays > 0;
      if (Math.abs(diffDays) > 30) {
        const human = selected.toLocaleDateString(localeCode);
        const msg = isBackdated
          ? tt(`Seçtiğiniz işlem tarihi (${human}) bugünden ${Math.abs(diffDays)} gün öncedir. Devam etmek istediğinize emin misiniz?`, `The selected transaction date (${human}) is ${Math.abs(diffDays)} days before today. Are you sure you want to continue?`)
          : tt(`Seçtiğiniz işlem tarihi (${human}) bugünden ${diffDays} gün sonradır. Devam etmek istediğinize emin misiniz?`, `The selected transaction date (${human}) is ${diffDays} days after today. Are you sure you want to continue?`);
        const ok = window.confirm(msg);
        if (!ok) {
          setLoading(false);
          return;
        }
      }
      // Bugün seçildiyse şimdiki saat, geçmiş/gelecek seçildiyse o günün öğleni (timezone'a güvenli)
      const finalDate = (diffDays === 0)
        ? new Date()
        : new Date(selected.getFullYear(), selected.getMonth(), selected.getDate(), 12, 0, 0);

      // ✅ Tarihi Türkiye yerel saatiyle (timezone kaymadan) gönder.
      // toISOString() UTC'ye çevirip 3 saat geriye kaydırıyordu; backend UTC+3 yerel saat
      // konvansiyonu kullandığı için hareketler 3 saat geride kaydolup listede aşağı düşüyordu.
      const pad2 = (n: number) => String(n).padStart(2, '0');
      const finalDateIso = `${finalDate.getFullYear()}-${pad2(finalDate.getMonth() + 1)}-${pad2(finalDate.getDate())}T${pad2(finalDate.getHours())}:${pad2(finalDate.getMinutes())}:${pad2(finalDate.getSeconds())}`;

      const collectionPayload: any = {
        companyId: companyId,
        accountId: accountId,
        accountName: accountName,
        amount: amountValue,
        currency: selectedCurrency.code,
        paymentMethod: selectedPaymentMethod,
        cashboxId: null,
        cashboxName: null,
        bankAccountId: null,
        bankSubAccountId: null,
        bankAccountName: null,
        description: description || tt(`${accountName} carisinden ${selectedPaymentMethod === 'accrual' ? 'tahakkuk ' : ''}tahsilat`, `${selectedPaymentMethod === 'accrual' ? 'Accrual ' : ''}collection from ${accountName}`),
        documentNo: documentNo.trim(), // Belge numarasını ekle
        date: finalDateIso, // ✅ Türkiye yerel saati (geri/ileri tarihli işlem desteği)
        createdBy: userDetails?.email || tt('Sistem', 'System'),
        // Şube: gönderilmezse backend cari hesabın şubesine düşer
        branchId: selectedBranchId || null,
        // PYS: proje bağı; backend kasa/banka yansımasına da taşır
        projectId: projectId,
      };
      
      if (selectedPaymentMethod === 'cash' && selectedCashbox) {
        collectionPayload.cashboxId = selectedCashbox.id.replace('cashbox_', '');
        collectionPayload.cashboxName = selectedCashbox.name;
      }
      
      if (selectedPaymentMethod !== 'cash' && selectedPaymentMethod !== 'accrual' && selectedBankAccount) {
        if (selectedBankAccount.isSubAccount) {
          collectionPayload.bankAccountId = selectedBankAccount.parentBankAccountId;
          collectionPayload.bankSubAccountId = selectedBankAccount.id.replace('sub_', '');
          collectionPayload.bankAccountName = selectedBankAccount.bankName;
        } else {
          collectionPayload.bankAccountId = selectedBankAccount.id.replace('bank_', '');
          collectionPayload.bankSubAccountId = null;
          collectionPayload.bankAccountName = selectedBankAccount.bankName;
        }
      }
      
      const response = await accountingApi.post(ACCOUNTING_API.TRANSACTION_COLLECTION, collectionPayload);

      const result = response.data;
      console.log('✅ Tahsilat başarılı:', result);
      
      // ✅ İşlem başarılı - Tahsilat makbuzu için transaction objesini oluştur
      const transactionData = {
        id: result.transactionId || result.id || 'temp',
        accountId: accountId,
        accountName: accountName,
        transactionType: 'COLLECTION',
        type: 'COLLECTION',
        amount: amountValue,
        debitAmount: 0,
        creditAmount: amountValue,
        description: description || tt(`${accountName} carisinden ${selectedPaymentMethod === 'accrual' ? 'tahakkuk ' : ''}tahsilat`, `${selectedPaymentMethod === 'accrual' ? 'Accrual ' : ''}collection from ${accountName}`),
        documentNo: documentNo.trim(),
        date: finalDateIso, // ✅ Kullanıcı seçtiği işlem tarihi (Türkiye yerel saati)
        createdAt: new Date().toISOString(),
        isIncome: true,
        currency: selectedCurrency.code,
        paymentMethod: selectedPaymentMethod,
        cashboxId: collectionPayload.cashboxId,
        cashboxName: collectionPayload.cashboxName,
        bankAccountId: collectionPayload.bankAccountId,
        bankAccountName: collectionPayload.bankAccountName,
      };
      
      setCompletedTransaction(transactionData);
      setSuccess(true);
      setSuccessMessage(tt('Tahsilat başarıyla kaydedildi!', 'Collection has been saved successfully!'));
      
      // ✅ 1.5 saniye sonra success ekranını kapat ve tahsilat makbuzunu göster
      setTimeout(() => {
        setSuccess(false);
        setShowReceiptModal(true);
      }, 1500);
      
    } catch (error) {
      console.error('❌ Tahsilat hatası:', error);
      setError(error instanceof Error ? error.message : tt('Tahsilat işlemi sırasında bir hata oluştu', 'An error occurred during collection transaction'));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSuccess(false);
  };

  const handleCloseModal = () => {
    onClose();
  };
  
  // Tahsilat makbuzu modalını kapat ve ana modalı kapat
  const handleCloseReceiptModal = () => {
    setShowReceiptModal(false);
    setCompletedTransaction(null);
    if (onSuccess) onSuccess();
    onClose();
  };

  // Kasa/Banka alanı görünür mü? (Tahakkukta gizlenir)
  const showCashboxBankField = selectedPaymentMethod !== 'accrual';

  return (
    <>
      <Dialog
        open={open}
        onClose={loading ? undefined : handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{tt('Cariden Tahsilat', 'Collection from Current Account')}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GuideButton guideKey="accounts.collectPayment" color="inherit" disabled={loading} />
              <IconButton edge="end" color="inherit" onClick={handleCloseModal} disabled={loading}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {success ? (
            <Box textAlign="center" p={3}>
              <CheckCircleOutlineIcon color="success" style={{ fontSize: 60 }} />
              <Typography variant="h6" color="success" gutterBottom sx={{ mt: 2 }}>
                {successMessage}
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Cari Hesap Bilgisi */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {tt('Cari Hesap', 'Current Account')}
                </Typography>
                <Typography variant="h6" gutterBottom>{accountName}</Typography>
                <Typography 
                  variant="body1" 
                  color={accountBalance >= 0 ? "success.main" : "error.main"} 
                  fontWeight="bold"
                >
                  {tt('Bakiye', 'Balance')}: {formatCurrency(accountBalance)}
                </Typography>
              </Paper>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Para Birimi */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="currency-select-label">{tt('Para Birimi *', 'Currency *')}</InputLabel>
                <Select
                  labelId="currency-select-label"
                  value={selectedCurrency?.code || ''}
                  label={tt('Para Birimi *', 'Currency *')}
                  onChange={(e) => {
                    const currency = currencies.find(c => c.code === e.target.value);
                    setSelectedCurrency(currency || null);
                  }}
                  disabled={loading}
                >
                  {currencies.map(currency => (
                    <MenuItem key={currency.id} value={currency.code}>
                      {currency.name} ({currency.symbol})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Ödeme Yöntemi */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="payment-method-select-label">{tt('Ödeme Yöntemi *', 'Payment Method *')}</InputLabel>
                <Select
                  labelId="payment-method-select-label"
                  value={selectedPaymentMethod}
                  label={tt('Ödeme Yöntemi *', 'Payment Method *')}
                  onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                  disabled={loading}
                >
                  {paymentMethods.map(method => (
                    <MenuItem key={method.id} value={method.id}>
                      {isTr ? method.tr : method.en}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Şube Seçimi */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="branch-select-label">{tt('Şube', 'Branch')}</InputLabel>
                <Select
                  labelId="branch-select-label"
                  value={selectedBranchId}
                  label={tt('Şube', 'Branch')}
                  onChange={(e) => setSelectedBranchId(e.target.value)}
                  disabled={loading || branchLocked}
                >
                  <MenuItem value="">
                    <em>{tt('Genel (cari şubesi)', 'General (account branch)')}</em>
                  </MenuItem>
                  {branches.map(branch => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Kasa/Banka Seçimi (Dinamik) — Tahakkukta gizlenir */}
              {showCashboxBankField && (
              <>
              {selectedPaymentMethod === 'cash' ? (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="cashbox-select-label">{tt('Kasa Seçin *', 'Select Cashbox *')}</InputLabel>
                  <Select
                    labelId="cashbox-select-label"
                    value={selectedCashbox?.id || ''}
                    label={tt('Kasa Seçin *', 'Select Cashbox *')}
                    onChange={(e) => {
                      const cashbox = filteredCashboxes.find(c => c.id === e.target.value);
                      setSelectedCashbox(cashbox || null);
                    }}
                    disabled={loading || filteredCashboxes.length === 0}
                  >
                    {filteredCashboxes.length === 0 ? (
                      <MenuItem disabled value="">
                        {tt('Uygun kasa bulunamadı', 'No suitable cashbox found')}
                      </MenuItem>
                    ) : (
                      filteredCashboxes.map(cashbox => (
                        <MenuItem key={cashbox.id} value={cashbox.id}>
                          {cashbox.name} ({tt('Bakiye', 'Balance')}: {cashbox.balance.toLocaleString(localeCode)} {cashbox.currencySymbol})
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              ) : (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="bank-select-label">{tt('Banka Hesabı Seçin *', 'Select Bank Account *')}</InputLabel>
                  <Select
                    labelId="bank-select-label"
                    value={selectedBankAccount?.id || ''}
                    label={tt('Banka Hesabı Seçin *', 'Select Bank Account *')}
                    onChange={(e) => {
                      const account = filteredBankAccounts.find(a => a.id === e.target.value);
                      setSelectedBankAccount(account || null);
                    }}
                    disabled={loading || filteredBankAccounts.length === 0}
                  >
                    {filteredBankAccounts.length === 0 ? (
                      <MenuItem disabled value="">
                        {tt('Uygun banka hesabı bulunamadı', 'No suitable bank account found')}
                      </MenuItem>
                    ) : (
                      filteredBankAccounts.map(account => (
                        <MenuItem key={account.id} value={account.id}>
                          {account.bankName} ({tt('Bakiye', 'Balance')}: {account.balance.toLocaleString(localeCode)} {account.currencySymbol})
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              )}
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                ℹ️ {selectedPaymentMethod === 'cash' ? tt('Nakit tahsilatlar için kasa seçiniz', 'Select a cashbox for cash collections') : tt('Banka tahsilatları için banka hesabı seçiniz', 'Select a bank account for bank collections')}
              </Typography>
              </>
              )}

              {/* Tahakkuk Bilgisi */}
              {selectedPaymentMethod === 'accrual' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {tt('Tahakkuk tahsilatı seçildiğinde, kasa veya banka bakiyesi değişmez, sadece cari hesap hareketleri kaydedilir.', 'When accrual collection is selected, cashbox or bank balance does not change; only current account transactions are recorded.')}
                </Alert>
              )}

              {/* İşlem Tarihi (geri/ileri tarihli işlem desteği) */}
              <AppDatePicker
                label={tt('İşlem Tarihi *', 'Transaction Date *')}
                value={transactionDate}
                onChange={setTransactionDate}
                fullWidth
                size="medium"
                sx={{ mb: 2 }}
                required
                disabled={loading}
                helperText={tt('İleri/geri tarihli işlem yapabilirsiniz; varsayılan olarak bugündür.', 'You can record back-dated or future transactions; defaults to today.')}
              />

              {/* Belge Numarası */}
              <TextField
                label={tt('Belge Numarası *', 'Document Number *')}
                value={documentNo}
                onChange={(e) => setDocumentNo(e.target.value)}
                fullWidth
                placeholder={tt('Örn: THB-2024-001', 'Ex: THB-2024-001')}
                sx={{ mb: 2 }}
                required
                disabled={loading}
                helperText={tt('Tahsilat makbuzu için belge numarası giriniz', 'Enter document number for collection receipt')}
              />

              {/* Miktar — Türkçe formatlama (1.000,00) */}
              <TextField
                label={tt('Miktar *', 'Amount *')}
                value={amount}
                onChange={handleAmountChange}
                fullWidth
                type="text"
                placeholder="0,00"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: selectedCurrency && (
                    <InputAdornment position="start">
                      {selectedCurrency.symbol}
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  inputMode: 'decimal',
                  autoComplete: 'off',
                }}
                required
                disabled={loading}
              />

              {/* Açıklama */}
              <TextField
                label={tt('Açıklama (Opsiyonel)', 'Description (Optional)')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder={tt('Tahsilat ile ilgili açıklama...', 'Description about collection...')}
                disabled={loading}
              />

              {/* Proje bağı (PYS) */}
              <ProjectSelector value={projectId} onChange={setProjectId} disabled={loading} size="medium" />
            </Box>
          )}
        </DialogContent>
        
        {!success && (
          <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
            <Button
              onClick={handleCloseModal}
              disabled={loading}
            >
              {tt('İptal', 'Cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={loading || !selectedCurrency || !amount || !documentNo || !transactionDate || (showCashboxBankField && selectedPaymentMethod === 'cash' && !selectedCashbox) || (showCashboxBankField && selectedPaymentMethod !== 'cash' && !selectedBankAccount)}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? tt('İşleniyor...', 'Processing...') : tt('💰 Tahsilatı Tamamla', '💰 Complete Collection')}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      
      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={successMessage}
      />
      
      {/* Tahsilat Makbuzu Modalı */}
      {completedTransaction && (
        <PaymentReceiptModal
          open={showReceiptModal}
          onClose={handleCloseReceiptModal}
          transaction={completedTransaction}
          companyId={companyId}
        />
      )}
    </>
  );
};

export default CollectPaymentModal;
